import React, { useState } from "react";
import Select from "react-select";

export const toLayerParameterValue = (layer) => ({
  layer: layer.layer_name,
  datatype: layer.datatype_name,
});

const findSelectedLayer = (layers, value) => {
  if (!value || typeof value !== "object") return null;

  return (
    layers.find(
      (layer) =>
        layer.layer_name === value.layer &&
        layer.datatype_name === value.datatype
    ) ||
    layers.find(
      (layer) =>
        String(layer.layer_number) === String(value.layerNumber) &&
        String(layer.datatype_number) === String(value.datatypeNumber)
    ) ||
    null
  );
};

const LayerValueSelect = ({
  layers = [],
  value,
  onChange,
  height = 30,
  hasError = false,
  placeholder = "Select layer...",
  compactSelectedValue = false,
  alignMenuRight = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const fontSize = height <= 27 ? "11px" : "12px";
  const innerHeight = `${Math.max(22, height - 2)}px`;

  return (
    <Select
      isSearchable
      options={layers}
      value={findSelectedLayer(layers, value)}
      placeholder={isOpen ? "Search layer by name..." : placeholder}
      controlShouldRenderValue={!isOpen}
      onMenuOpen={() => setIsOpen(true)}
      onMenuClose={() => setIsOpen(false)}
      onChange={(selected) => {
        if (selected) onChange(toLayerParameterValue(selected));
      }}
      getOptionLabel={(layer) =>
        `${layer.layer_name || ""} ${layer.datatype_name || ""}`.trim()
      }
      getOptionValue={(layer) =>
        `${layer.layer_number}:${layer.datatype_number}`
      }
      filterOption={(option, inputValue) => {
        const search = String(inputValue || "").trim().toLowerCase();
        const layerName = String(option.data.layer_name || "").toLowerCase();
        const datatypeName = String(
          option.data.datatype_name || ""
        ).toLowerCase();
        return layerName.includes(search) || datatypeName.includes(search);
      }}
      formatOptionLabel={(layer, { context }) => (
        <div
          title={`${layer.layer_name || ""} ${layer.datatype_name || ""}`.trim()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            minWidth: 0,
          }}
        >
          {(!compactSelectedValue || context !== "value") && (
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: layer.color || "#ccc",
                border: "1px solid #8b949e",
                borderRadius: "3px",
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              color: "#fff",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: compactSelectedValue && context === "value" ? 1 : undefined,
              minWidth: 0,
              fontSize:
                compactSelectedValue && context === "value" ? "10px" : undefined,
            }}
          >
            {layer.layer_name}
          </span>
          {(!compactSelectedValue || context !== "value") && (
            <span
              style={{
                color: "#9aa4ad",
                marginLeft: "auto",
                whiteSpace: "nowrap",
                fontSize: "10px",
              }}
            >
              {layer.datatype_name}
            </span>
          )}
        </div>
      )}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No matching layer found" : "No layers available"
      }
      menuPlacement="auto"
      styles={{
        container: (base) => ({
          ...base,
          flex: 1,
          width: "100%",
          minWidth: 0,
        }),
        control: (base, state) => ({
          ...base,
          backgroundColor: "#2a2a2a",
          borderColor: hasError
            ? "#ff3b3b"
            : state.isFocused
              ? "#00bcd4"
              : "#444",
          borderWidth: hasError ? "2px" : "1px",
          minHeight: `${height}px`,
          height: `${height}px`,
          fontSize,
          borderRadius: "3px",
          boxShadow: state.isFocused
            ? "0 0 0 1px rgba(0, 188, 212, 0.2)"
            : "none",
          cursor: "pointer",
          "&:hover": {
            borderColor: hasError
              ? "#ff3b3b"
              : state.isFocused
                ? "#00bcd4"
                : "#666",
          },
        }),
        valueContainer: (base) => ({
          ...base,
          padding: compactSelectedValue ? "0 3px" : "0 6px",
          height: innerHeight,
          minWidth: 0,
        }),
        input: (base) => ({
          ...base,
          color: "#fff",
          margin: 0,
          padding: 0,
          fontSize,
        }),
        placeholder: (base) => ({
          ...base,
          color: "#8b949e",
          fontSize,
          whiteSpace: "nowrap",
        }),
        indicatorsContainer: (base) => ({ ...base, height: innerHeight }),
        dropdownIndicator: (base, state) => ({
          ...base,
          padding: compactSelectedValue ? "2px" : "3px 5px",
          color: state.isFocused ? "#00bcd4" : "#9aa4ad",
          transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
          transition: "transform 0.15s ease",
          svg: { width: "13px", height: "13px" },
        }),
        indicatorSeparator: () => ({ display: "none" }),
        menu: (base) => ({
          ...base,
          minWidth: "220px",
          ...(alignMenuRight ? { left: "auto", right: 0 } : {}),
          marginTop: "4px",
          backgroundColor: "#232323",
          border: "1px solid #444",
          borderRadius: "5px",
          boxShadow: "0 8px 20px rgba(0, 0, 0, 0.4)",
          overflow: "hidden",
          zIndex: 9999,
        }),
        menuList: (base) => ({
          ...base,
          padding: "4px",
          maxHeight: "220px",
          "::-webkit-scrollbar": { width: "5px" },
          "::-webkit-scrollbar-thumb": {
            background: "#555",
            borderRadius: "3px",
          },
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? "#124b55"
            : state.isFocused
              ? "#333"
              : "transparent",
          color: "#fff",
          fontSize: "12px",
          padding: "8px 9px",
          borderRadius: "3px",
          cursor: "pointer",
        }),
        singleValue: (base) => ({
          ...base,
          color: "#fff",
          minWidth: 0,
          fontSize,
        }),
        noOptionsMessage: (base) => ({
          ...base,
          color: "#9aa4ad",
          fontSize: "12px",
        }),
      }}
    />
  );
};

export default LayerValueSelect;
